import React, { useMemo } from "react";

import { Card, Button, Tooltip } from "antd";
import {
  CalendarOutlined,
  EnvironmentOutlined,
  TeamOutlined,
} from "@ant-design/icons";

import { EventActionsMenu } from "./EventActionsMenu";
import { getEventActions } from "./eventActionConfig";
import { getStatusConfig, getStatusMessage, isApproved } from "./util/eventStatusConfig";

/**
 * Determines whether "now" falls within the event capture window.
 * - startDate is treated as 00:00 of that day
 * - endDate is treated as 23:59 of that day
 */
/**
 * Determines whether "now" falls within the event capture window.
 *
 * Rules:
 * - Base window: startDate 00:00 → endDate 23:59 (Approved only)
 * - Owner grace: +13 days after endDate if not Concluded or Cancelled
 * - On day 14 after endDate, capture must stop
 */
export const isWithinEventCaptureWindow = (event, now = Date.now()) => {
  if (!event?.startDate || !event?.endDate || !event?.status?.value) {
    return false;
  }

  const status = event.status.value;
  // Only Approved events can capture
  if (!isApproved(status)) {
    return false;
  }

  const start = new Date(event.startDate);
  if (Number.isNaN(start.getTime())) return false;
  start.setHours(0, 0, 0, 0);

  const end = new Date(event.endDate);
  if (Number.isNaN(end.getTime())) return false;
  end.setHours(23, 59, 59, 999);

  const nowTime = now;
  // Normal capture window
  if (nowTime >= start.getTime() && nowTime <= end.getTime()) {
    return true;
  }

  // Owner grace period logic
  if (event.owner === true && isApproved(status)) {
    const graceEnd = new Date(end);
    graceEnd.setDate(graceEnd.getDate() + 13);
    graceEnd.setHours(23, 59, 59, 999);

    return nowTime > end.getTime() && nowTime <= graceEnd.getTime();
  }

  return false;
};


/**
 * CTA STATE LOGIC (in-component style helper)
 */
const getCaptureAttendeesState = (event) => {
  const status = event?.status?.value;
  const statusText = event?.statusText;
  const config = getStatusConfig(status);
  const now = Date.now();

  // Unknown status
  if (!config) {
    return {
      enabled: false,
      tooltip: `Capture not available (status: ${statusText})`,
    };
  }

  // Status does not allow capture
  if (!config.canCapture) {
    return {
      enabled: false,
      tooltip: config.message || getStatusMessage(status),
    };
  }

  // Approved-like flow (config-driven)
  const withinWindow = isWithinEventCaptureWindow(event, now);

  if (withinWindow) {
    return { enabled: true, tooltip: "" };
  }

  const start = new Date(event.startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(event.endDate);
  end.setHours(23, 59, 59, 999);

  // Time-based messaging (only remaining conditional logic)
  if (now < start.getTime()) {
    return {
      enabled: false,
      tooltip: "Capture opens when the event starts.",
    };
  }

  if (now > end.getTime() && !event.owner) {
    return {
      enabled: false,
      tooltip: "Capture closed after the event ended.",
    };
  }

  if (now > end.getTime() && event.owner) {
    return {
      enabled: false,
      tooltip:
        "Capture available only during the owner grace period (up to 13 days).",
    };
  }

  return {
    enabled: false,
    tooltip: "Capture is not available at this time.",
  };
};


export const EventCard = ({
  event,
  statusStyle,
  onPrimaryAction,
  onMenuAction,
}) => {

  const actions = useMemo(
    () => getEventActions(event),
    [event]
  );


 const captureState = useMemo(
    () => getCaptureAttendeesState(event),
    [event]
  );


  return (
    <Card className="event-card" data-testid={`event-card-${event.id}`}>
      <div className="event-card__row">
        <Tooltip
          title={`Status: ${event.statusText}`}
          placement="top"
          mouseEnterDelay={0.2}
          trigger={["hover", "click"]}
        >
          <div
            className="event-card__status"
            style={{ background: statusStyle?.bg }}
          >
            {statusStyle?.icon}
          </div>
        </Tooltip>

        <div className="event-card__content">
          <div className="event-card__header">
            <div className="event-card__title" data-testid="event-card-title">
              {`${event.title}`}
            </div>

            <div className="event-card__actions">
              <EventActionsMenu
                actions={actions}
                onAction={(action) => onMenuAction(action, event)}
              />
            </div>
          </div>

          <div className="event-card__text-block">
            <div className="event-card__description">
              <EnvironmentOutlined />
              {`${event.venue?.name} (${event.venue?.cityOrTown}, ${event.venue?.province})`}
            </div>
          </div>

          <div className="event-card__meta">
            <span>
              <CalendarOutlined /> {event?.dates}
            </span>

            {event.participants > 0 && (
              <span>
                <TeamOutlined /> {event.participants}
              </span>
            )}
          </div>

          {/* ALWAYS VISIBLE CTA */}
          <div className="event-card__footer">
            <Tooltip
              title={captureState.enabled ? "" : captureState.tooltip}
              placement="top"
              trigger={["hover", "click"]}
            >
              <Button
                type={captureState.enabled ? "primary" : "default"} // visual differentiation
                data-testid="capture-attendees-btn"
                data-event-id={event.id}
                className={`events-btn events-btn--block events-btn--md ${captureState.enabled ? "" : "events-btn--disabled"}`}
                onClick={() => {
                  if (captureState.enabled) {
                    onPrimaryAction(event);
                  }
                }}
              >
                Capture Attendees
              </Button>
            </Tooltip>
          </div>
        </div>
      </div>
    </Card>
  );
};
